"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
function PartnerFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="222017" className="flex flex-col items-center justify-center py-12 px-6 text-center animate-in fade-in zoom-in duration-500">
        <div className="h-16 w-16 bg-secondary/20 text-secondary rounded-full flex items-center justify-center mb-6">
          <CircleCheck className="h-8 w-8" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground mb-2 uppercase tracking-tight">Inquiry Received</h3>
        <p className="text-muted-foreground font-sans mb-8 max-w-sm">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="w-full sm:w-auto border-secondary text-secondary hover:bg-secondary hover:text-primary transition-colors rounded-sm">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="company_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground uppercase text-xs tracking-wider">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="James Harrison" className="h-11 bg-transparent border-0 border-b border-border rounded-none focus-visible:ring-0 focus-visible:border-secondary transition-colors px-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="contact_name" rules={{
        required: "Location is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground uppercase text-xs tracking-wider">Project Location</FormLabel>
              <FormControl>
                <Input placeholder="London, UK" className="h-11 bg-transparent border-0 border-b border-border rounded-none focus-visible:ring-0 focus-visible:border-secondary transition-colors px-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground uppercase text-xs tracking-wider">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="j.harrison@example.com" className="h-11 bg-transparent border-0 border-b border-border rounded-none focus-visible:ring-0 focus-visible:border-secondary transition-colors px-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground uppercase text-xs tracking-wider">Phone Number <span className="text-muted-foreground font-normal text-[10px] ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 20 7946 0000" className="h-11 bg-transparent border-0 border-b border-border rounded-none focus-visible:ring-0 focus-visible:border-secondary transition-colors px-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="partnership_type" rules={{
      required: "Please select a project type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-semibold text-foreground uppercase text-xs tracking-wider">Project Type</FormLabel>
            <FormControl>
              <div className="relative">
                <select className="flex h-11 w-full min-w-0 rounded-none border-0 border-b border-border bg-transparent px-0 py-2 text-base shadow-none transition-colors outline-none focus-visible:border-secondary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm appearance-none" {...field}>
                  <option value="" disabled>Select project type</option>
                  <option value="New Build">New Build Home</option>
                  <option value="Extension">Residential Extension</option>
                  <option value="Renovation">Full Renovation</option>
                  <option value="Bespoke">Bespoke Development</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-0 text-muted-foreground">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-semibold text-foreground uppercase text-xs tracking-wider">Project Details <span className="text-muted-foreground font-normal text-[10px] ml-1">(Optional)</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your vision, budget range, and timeline..." className="min-h-[120px] resize-y bg-transparent border-0 border-b border-border rounded-none focus-visible:ring-0 focus-visible:border-secondary transition-colors px-0" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm flex items-start gap-3 text-destructive">
          <CircleAlert className="h-5 w-5 shrink-0 mt-0.5" />
          <p className="text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-sm font-default font-bold uppercase tracking-widest bg-primary text-secondary border border-secondary hover:bg-secondary hover:text-primary transition-all rounded-none">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            Processing...
          </> : 'Send Inquiry'}
      </Button>
    </Form>;
}
export default function PartnerWithUs() {
  return <section id="lets-discuss-your-vision" className="w-full bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-secondary/5 -skew-x-12 translate-x-20 hidden lg:block pointer-events-none" />
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-6xl mx-auto rounded-sm overflow-hidden shadow-none border border-border bg-card flex flex-col lg:flex-row">
          
          {}
          <div className="lg:w-5/12 relative min-h-[300px] lg:min-h-full hidden md:block">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1646431/img/luxury_residential_construction_site.png" alt="High-end residential construction site with architectural precision" className="absolute inset-0 w-full h-full object-cover grayscale contrast-125" />
            {}
            <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent" />
            <div className="absolute inset-0 border-r border-secondary/20" />
            
            <div className="absolute bottom-0 left-0 p-8 text-foreground">
              <h3 className="text-2xl font-default font-black mb-2 uppercase tracking-tighter text-secondary">Structural Integrity</h3>
              <p className="text-muted-foreground font-sans text-sm max-w-xs leading-relaxed">
                Delivering bespoke residential developments with uncompromising craftsmanship and technical excellence.
              </p>
            </div>
          </div>

          {}
          <div className="lg:w-7/12 p-8 md:p-12 lg:p-14">
            <div className="max-w-xl mx-auto">
              <div className="mb-10">
                <h2 className="text-3xl md:text-5xl font-default font-black text-foreground mb-4 tracking-tighter uppercase">
                  Let's Discuss Your Vision
                </h2>
                <p className="text-muted-foreground font-sans text-lg leading-relaxed">
                  From new-build homes to high-end renovations, our team is ready to bring your architectural plans to life.
                </p>
              </div>

              <FormProvider formKey="vision_form_start_your_build" formId="vision_form_start_your_build" sectionName="Partner With Us" defaultValues={{
              company_name: "",
              contact_name: "",
              email: "",
              phone: "",
              partnership_type: "",
              message: ""
            }} submitText="Send Inquiry" successMessage="Your inquiry has been received. A project manager from Glencross Construction will contact you within 24 hours.">
                <PartnerFormInner />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}